import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
  Chip,
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Add, 
  Description,
  Search,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Category,
  ListAlt,
  Update
} from '@mui/icons-material';

const FormTypeMaster = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [addFormTypeDialogOpen, setAddFormTypeDialogOpen] = useState(false);
  const [editFormTypeDialogOpen, setEditFormTypeDialogOpen] = useState(false);
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [formTypeName, setFormTypeName] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  // Sample form type data
  const [formTypeData, setFormTypeData] = useState([
    {
      id: 1,
      formType: 'Employee Registration Form',
      createdDate: '2024-01-15',
      status: 'Active',
    },
    {
      id: 2,
      formType: 'Plot Booking Form',
      createdDate: '2024-02-20',
      status: 'Active',
    },
    {
      id: 3,
      formType: 'Payment Receipt Form',
      createdDate: '2024-03-10',
      status: 'Active',
    },
    {
      id: 4,
      formType: 'Customer Registration Form',
      createdDate: '2024-04-05',
      status: 'Active',
    },
    {
      id: 5,
      formType: 'Site Visit Form',
      createdDate: '2024-05-12',
      status: 'Inactive',
    },
    {
      id: 6,
      formType: 'Complaint Registration Form',
      createdDate: '2024-06-18',
      status: 'Active',
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAddFormType = () => {
    setFormTypeName('');
    setAddFormTypeDialogOpen(true);
  };

  const handleEditFormType = (formType) => {
    setSelectedFormType(formType);
    setFormTypeName(formType.formType);
    setEditFormTypeDialogOpen(true);
  };

  const handleDeleteFormType = (formTypeId) => {
    if (window.confirm('Are you sure you want to delete this form type?')) {
      setFormTypeData(prev => prev.filter(item => item.id !== formTypeId));
      console.log('Deleting form type:', formTypeId);
    }
  };

  const handleSaveFormType = () => {
    if (!formTypeName.trim()) {
      alert('Please enter a form type name');
      return;
    }

    const newFormType = {
      id: Math.max(...formTypeData.map(item => item.id)) + 1,
      formType: formTypeName,
      createdDate: new Date().toISOString().split('T')[0],
      status: 'Active',
    };

    setFormTypeData(prev => [...prev, newFormType]);
    setAddFormTypeDialogOpen(false);
    setFormTypeName('');
    setSuccessMessage('Form type added successfully!');
  };

  const handleUpdateFormType = () => {
    if (!formTypeName.trim()) {
      alert('Please enter a form type name');
      return;
    }

    setFormTypeData(prev =>
      prev.map(item =>
        item.id === selectedFormType.id
          ? { ...item, formType: formTypeName }
          : item
      )
    );

    setEditFormTypeDialogOpen(false);
    setFormTypeName('');
    setSelectedFormType(null);
    setSuccessMessage('Form type updated successfully!');
  };

  const getFilteredFormTypeData = () => {
    let filteredData = formTypeData;

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(formType =>
        Object.values(formType).some(value =>
          String(value).toLowerCase().includes(tableSearchQuery.toLowerCase())
        )
      );
    }

    // Filter by tab selection
    if (tabValue === 1) {
      filteredData = filteredData.filter(formType => formType.status === 'Active');
    } else if (tabValue === 2) {
      filteredData = filteredData.filter(formType => formType.status === 'Inactive');
    }

    return filteredData;
  };

  const getFilteredByStatus = (status) => {
    return formTypeData.filter(formType => formType.status === status);
  };

  const getSearchSummary = () => {
    const filteredData = getFilteredFormTypeData();
    return `${filteredData.length} form type(s) found`;
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Active':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Inactive':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  // Summary calculations
  const totalFormTypes = formTypeData.length;
  const activeCount = getFilteredByStatus('Active').length;
  const inactiveCount = getFilteredByStatus('Inactive').length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Description sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          Form Type Master Management
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <ListAlt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Form Types
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalFormTypes}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Active Form Types
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {activeCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Warning sx={{ color: '#d32f2f', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Inactive Form Types
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {inactiveCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Card */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <Category sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Form Type Management
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Search Form Types..."
                variant="outlined"
                size="small"
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddFormType}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                Add Form Type
              </Button>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              {getSearchSummary()}
            </Box>
          </Alert>

          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="form type status tabs">
              <Tab label="All Form Types" />
              <Tab label="Active" />
              <Tab label="Inactive" />
            </Tabs>
          </Box>
        
        {getFilteredFormTypeData().length > 0 ? (
          <TableContainer sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Form Type Details</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Created Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredFormTypeData()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((formType, index) => (
                    <TableRow key={formType.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>{page * rowsPerPage + index + 1}</TableCell>
                      
                      {/* Form Type Details */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Description sx={{ color: '#666', fontSize: 16 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                              {formType.formType}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Created Date */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday sx={{ color: '#666', fontSize: 16 }} />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                            📅 {formType.createdDate}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={formType.status}
                          sx={{
                            ...getStatusStyle(formType.status),
                            fontWeight: 600,
                            fontSize: '12px'
                          }}
                          size="small"
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit Form Type">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEditFormType(formType)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Form Type">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleDeleteFormType(formType.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={getFilteredFormTypeData().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        ) : (
          <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Warning sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  No Form Types Found
                </Typography>
                <Typography variant="body2">
                  No form types match your search criteria.
                </Typography>
              </Box>
            </Box>
          </Alert>
        )}
        </CardContent>
      </Card>

      {/* Add Form Type Dialog */}
      <Dialog open={addFormTypeDialogOpen} onClose={() => setAddFormTypeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Add sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Add New Form Type
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Form Type Name *"
              value={formTypeName}
              onChange={(e) => setFormTypeName(e.target.value)}
              placeholder="Enter form type name"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFormTypeDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveFormType} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Form Type Dialog */}
      <Dialog open={editFormTypeDialogOpen} onClose={() => setEditFormTypeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Update sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
              Edit Form Type
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Form Type Name *"
              value={formTypeName}
              onChange={(e) => setFormTypeName(e.target.value)}
              placeholder="Enter form type name"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFormTypeDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdateFormType} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormTypeMaster;